import React from "react";
import { ActivityIndicator, ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { RouteProp, useFocusEffect, useIsFocused, useNavigation } from "@react-navigation/native";
import { useSelector } from "react-redux";

import { tBoutique, tUserConnection } from "tmt_api/src/types/types";

import * as connectionDb from "src/db/connection.db";
import * as retailerDb from "src/db/retailer.db";
import { PdArrowUpRight, PdFacebookAlt, PdInstagram, PdLinkedin, PdMessage, PdTruck } from "src/components/Icons";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { formatPercentNumber, longAddressToString } from "src/util/gFormatting";
import { useAppDispatch } from "src/redux";
import { PdBadge, PdBadgeLabel, PdStack, PdText } from "src/components/layout";
import { setCurrentThread } from "src/redux/messages/actions";
import { kRoute } from "src/kRoute";
import { getDefaultAddress } from "src/util/user";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { PdFieldset, PdFormLabel } from "src/components/form-control";
import {
  PdHookFormCheckbox,
  PdHookFormErrorMessage,
  PdHookFormInput,
  PdHookFormInputPercentage,
} from "src/components/hook-form";
import { PdCheckboxLabel } from "src/components/forms/checkbox";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdDivider } from "src/components/layout/table";
import { kEmailRegex } from "src/util/constants";
import { useConfirmModal } from "src/hooks/modals";
import {
  acceptConnectionRequest,
  cancelConnectionRequest,
  deactivateConnection,
  declineConnectionRequest,
} from "src/redux/connectionManager/actions";
import { cloneDeep } from "lodash";
import { updateConnectionData } from "src/redux/connections/actions";
import { openUrlInNewTab } from "src/util/linking";
import {
  PdProfileAvatar,
  PdProfileBanner,
  PdProfileCard,
  PdProfileSocialIconButton,
  PdProfileTag,
  PdProfileTagLabel,
} from "src/components/layout/display";
import { dRetailerCategories } from "tmt_api/src/shared/dics";

type tParams = {
  rConnection: {
    id: string;
    connectionId: string;
  };
};

type tRepStatus = "Active" | "Invited" | "Pending" | "Inactive";

const getThemeColorFromRepStatus = (status: tRepStatus): string => {
  if (status === "Inactive") return kColors.red200;
  if (status === "Pending") return kColors.pink;
  if (status === "Invited") return kColors.orange200;
  if (status === "Active") return kColors.green200;
  return kColors.gray300;
};

const useIsLargeScreenSelector = () => {
  return useSelector((state) => state.NavigationReducer.deviceSize === "lg");
};

type tConnectionTermsFormData = {
  shippingPercent: number | null;
  shippingAccount: string;
  salesRepEmail: string;
  salesRepCommission: number | null;
  isOrderMinimumRequired: boolean;
};

const getRepStatus = (connection: tUserConnection): tRepStatus => {
  if (!connection.rep?.id && !connection.rep?.email && !connection.rep?.vendorId) {
    return "Inactive";
  } else if (!connection.rep?.id && connection.rep?.email && !connection.rep?.vendorId) {
    return "Invited";
  } else if (connection.rep?.id && connection.rep?.email && !connection.rep?.vendorId) {
    return "Pending";
  } else if (connection.rep?.id && connection.rep?.email && connection.rep?.vendorId) {
    return "Active";
  }
  return "Inactive";
};

const ConnectionTermsForm: React.FC<{ connection: tUserConnection }> = ({ connection }) => {
  const navigation = useNavigation();
  const rIsLargeScreen = useIsLargeScreenSelector();
  const confirmModal = useConfirmModal();
  const dispatch = useAppDispatch();
  const rEstimatingShippingUrl = useSelector((state) => state.ConfigsReducer.data.urls?.estimatingShipping);
  const formMethods = useForm<tConnectionTermsFormData>({
    defaultValues: {
      shippingPercent: formatPercentNumber(connection.shipPercent),
      shippingAccount: connection.shipAccount ?? "",
      salesRepEmail: connection.rep?.email ?? "",
      salesRepCommission: formatPercentNumber(connection.repPercent),
      isOrderMinimumRequired: connection.isOpening,
    },
  });
  const watched = useWatch({
    control: formMethods.control,
    name: ["shippingPercent", "shippingAccount", "salesRepEmail", "salesRepCommission"],
  });

  const isConnectionApproved = connection.status === "Approved";
  const isConnectionDeclined = connection.status === "Declined";
  const isConnectionInvited = connection.status === "RequestToRetailer";
  const repStatus = getRepStatus(connection);

  const handleOnDecline = async () => {
    const response = await confirmModal.openAsync({
      title: "Decline Connection",
      description: "Are you sure you want decline this connection?",
      confirmButtonLabel: "Confirm",
    });
    if (response.status === "closed") return;

    dispatch(declineConnectionRequest({ brandId: connection.brand.id, boutiqueId: connection.boutique.id }));
  };

  const handleOnDeactivate = async () => {
    const response = await confirmModal.openAsync({
      title: "Deactivate Connection",
      description: "Once you Deactivate this Connection, they will no longer be able to order from you.",
      confirmButtonLabel: "Confirm",
    });
    if (response.status === "closed") return;

    dispatch(deactivateConnection({ brandId: connection.brand.id, boutiqueId: connection.boutique.id }));
  };

  const handleOnCancelRequest = async () => {
    const response = await confirmModal.openAsync({
      title: "Cancel Request",
      description: "Are you sure you want cancel the request for connection?",
      confirmButtonLabel: "Confirm",
    });
    if (response.status === "closed") return;

    dispatch(
      cancelConnectionRequest({
        brandId: connection.brand.id,
        boutiqueId: connection.boutique.id,
        connectionId: connection.uuid,
      }),
    );

    navigation.goBack();
  };

  const handleOnAcceptOrSave = formMethods.handleSubmit(async (formData) => {
    if (formData.salesRepEmail && connection.rep?.email !== formData.salesRepEmail) {
      const response = await confirmModal.openAsync({
        title: !connection.rep?.email ? "Assign Sales Rep" : "Change Sales Rep",
        description: !connection.rep?.email
          ? `Are you sure you want to add the Sales Rep ${formData.salesRepEmail} to this connection?`
          : `Are you sure you want to change the Sales Rep from ${connection?.rep?.email} to ${formData.salesRepEmail}?`,
        confirmButtonLabel: "Confirm",
      });
      if (response.status === "closed") return;
    }

    if (connection.rep?.email && !formData.salesRepEmail) {
      const response = await confirmModal.openAsync({
        title: "Delete Sales Rep from connection",
        description: `Are you sure you want to delete ${connection.rep?.email} from this connection?`,
        confirmButtonLabel: "Deactivate",
      });
      if (response.status === "closed") return;
    }

    const newConnection = cloneDeep(connection);
    const isPercentShippingAssignment = !!(
      (formData.shippingPercent || formData.shippingPercent === 0) &&
      !formData.shippingAccount
    );
    const shippingPercent = isPercentShippingAssignment ? (formData.shippingPercent ?? 0) / 100 : undefined;
    const shippingAccount = isPercentShippingAssignment ? "" : formData.shippingAccount;
    const salesRepCommission = formData.salesRepCommission === null ? undefined : formData.salesRepCommission / 100;
    newConnection.shipPercent = shippingPercent;
    newConnection.shipAccount = shippingAccount;
    newConnection.isOpening = formData.isOrderMinimumRequired;
    newConnection.repPercent = salesRepCommission;

    if (isConnectionApproved) {
      dispatch(updateConnectionData(newConnection, formData.salesRepEmail));
    } else {
      dispatch(
        acceptConnectionRequest({
          brandId: connection.brand.id,
          boutiqueId: connection.boutique.id,
          connectionArgs: {
            shippingPercent,
            shippingAccount,
            salesRepEmail: formData.salesRepEmail,
            salesRepCommission,
          },
        }),
      );
    }

    formMethods.setValue("shippingPercent", formatPercentNumber(shippingPercent));
    formMethods.setValue("shippingAccount", shippingAccount);
    formMethods.setValue("isOrderMinimumRequired", formData.isOrderMinimumRequired);
    formMethods.setValue("salesRepEmail", formData.salesRepEmail);
    formMethods.setValue("salesRepCommission", formatPercentNumber(salesRepCommission));
  });

  if (isConnectionInvited) {
    return (
      <View style={rIsLargeScreen ? [styles.mx20] : [styles.mx5, styles.mb6]}>
        <PdButton
          colorScheme="purple"
          onPress={handleOnCancelRequest}
          style={styles.cancelRequestButton}
          isFullWidth={!rIsLargeScreen}
        >
          <PdButtonLabel>Cancel Request</PdButtonLabel>
        </PdButton>
      </View>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <View style={rIsLargeScreen ? styles.webFormContainer : styles.mobileFormContainer}>
        <PdStack
          style={rIsLargeScreen ? styles.webFormLine : styles.mobileFormLine}
          layout={[1, 1]}
          gap={4}
          direction={rIsLargeScreen ? "horizontal" : "vertical"}
        >
          <View style={rIsLargeScreen ? styles.pr3 : {}}>
            <View style={styles.assignShippingFormControl}>
              <PdFieldset>Assign Shipping *</PdFieldset>
              {rEstimatingShippingUrl && (
                <PdButton
                  onPress={() => openUrlInNewTab(rEstimatingShippingUrl)}
                  leftIcon={<PdTruck style={styles.sizeSm} />}
                  variant="ghost"
                  style={styles.mlAuto}
                  contentContainerStyle={styles.px0}
                  size="sm"
                >
                  <PdButtonLabel style={styles.linkLabel}>Estimate Your shipping</PdButtonLabel>
                </PdButton>
              )}
            </View>

            <PdStack
              style={rIsLargeScreen ? styles.alignStart : {}}
              layout={[1, 1]}
              gap={4}
              direction={rIsLargeScreen ? "horizontal" : "vertical"}
            >
              <View>
                <View style={styles.groundShippingFormControl}>
                  <PdFormLabel>Ground Shipping</PdFormLabel>
                  {connection.shipPercent !== undefined && connection.shipPercent !== null && (
                    <PdBadge style={styles.activeBadge}>
                      <PdBadgeLabel>Active</PdBadgeLabel>
                    </PdBadge>
                  )}
                </View>
                <View style={styles.pdInputGroup}>
                  <PdHookFormInputPercentage
                    style={styles.pdInputGroupInput}
                    name="shippingPercent"
                    onChangeNumber={() => {
                      if (watched[1]) {
                        formMethods.setValue("shippingAccount", "");
                      }
                    }}
                    rules={{
                      validate: (value) =>
                        !watched[1] && !value && value !== 0 ? "Shipping assignment is mandatory" : true,
                    }}
                    onSubmitEditing={handleOnAcceptOrSave}
                  />
                  <PdText style={styles.mr2}>%</PdText>
                </View>
                <PdHookFormErrorMessage name="shippingPercent" />
              </View>
              <View>
                <View style={styles.shipAccountFormControl}>
                  <PdFormLabel>Retailer&apos;s Account No.</PdFormLabel>
                  {!!connection.shipAccount && (
                    <PdBadge style={styles.activeBadge}>
                      <PdBadgeLabel>Active</PdBadgeLabel>
                    </PdBadge>
                  )}
                </View>
                <PdHookFormInput
                  name="shippingAccount"
                  onChangeText={() => {
                    if (watched[0]) {
                      formMethods.setValue("shippingPercent", null);
                    }
                  }}
                  rules={{
                    validate: (value) => (watched[0] === null && !value ? "Shipping assignment is mandatory" : true),
                  }}
                  onSubmitEditing={handleOnAcceptOrSave}
                />
                <PdHookFormErrorMessage name="shippingAccount" />
              </View>
            </PdStack>
          </View>
          <View>
            <PdText variant="body-sm" style={styles.helpText}>
              {
                "Please enter a shipping percentage you'll charge this retailer per order. Shipping & handling you assign are paid in full to the brand; commissions and fees are not deducted from your shipping and handling costs."
              }
            </PdText>
            <PdText variant="body-sm" style={styles.helpTextEnd}>
              {
                "If you can accommodate using a Retailer's own shipping account number, please enter the info in the designated field. The Retailer will not be charged shipping fees at checkout and it's your responsibility to ensure you use the Retailer's shipping account info when fulfilling their orders. The shipping account number you enter will show on your invoices and packing slips for easy reference."
              }
            </PdText>
          </View>
        </PdStack>
        <PdDivider style={styles.my6} />
        <PdStack
          style={rIsLargeScreen ? styles.webFormLine : styles.mobileFormLine}
          layout={[1, 1]}
          gap={4}
          direction={rIsLargeScreen ? "horizontal" : "vertical"}
        >
          <View style={rIsLargeScreen ? styles.pr3 : {}}>
            <PdFieldset style={styles.mb4}>Assign Order Requirements</PdFieldset>
            <PdHookFormCheckbox name="isOrderMinimumRequired" isDisabled={connection.isFirstOrderPlaced}>
              <PdCheckboxLabel>Opening Order Minimum Required</PdCheckboxLabel>
            </PdHookFormCheckbox>
          </View>
          <View>
            <PdText variant="body-sm" style={styles.helpText}>
              {
                "Check this box if you'd like this Retailer's first order to meet your Opening Order minimum. After their opening order, all their subsequent orders will have to meet your Reorder minimum."
              }
            </PdText>
            <PdText variant="body-sm" style={styles.helpTextEnd}>
              {
                "Uncheck the box if you won't require this retailer to meet your Opening Order minimum with their first order. For example, if this is an existing retailer you currently work with and does not need to place an opening order."
              }
            </PdText>
          </View>
        </PdStack>
        <PdDivider style={styles.my6} />
        <PdStack
          style={rIsLargeScreen ? styles.webFormLine : styles.mobileFormLine}
          layout={[1, 1]}
          gap={4}
          direction={rIsLargeScreen ? "horizontal" : "vertical"}
        >
          <View style={rIsLargeScreen ? styles.pr3 : {}}>
            <PdFieldset style={styles.mb4}>Assign a Sales Rep to this Retailer</PdFieldset>
            <PdStack
              style={rIsLargeScreen ? styles.alignStart : {}}
              layout={[1, 1]}
              gap={4}
              direction={rIsLargeScreen ? "horizontal" : "vertical"}
            >
              <View>
                <View style={styles.salesRepFormControl}>
                  <PdFormLabel>Sales Rep</PdFormLabel>
                  {repStatus !== "Inactive" && (
                    <PdBadge style={[styles.ml2, { backgroundColor: getThemeColorFromRepStatus(repStatus) }]}>
                      <PdBadgeLabel>{repStatus}</PdBadgeLabel>
                    </PdBadge>
                  )}
                </View>
                <PdHookFormInput
                  name="salesRepEmail"
                  rules={{
                    pattern: {
                      value: kEmailRegex,
                      message: "Invalid email",
                    },
                    validate: (value) =>
                      !!watched[3] && !value ? "Sales Rep assignment is mandatory as you entered a commission" : true,
                  }}
                  onSubmitEditing={handleOnAcceptOrSave}
                />
                <PdHookFormErrorMessage name="salesRepEmail" />
              </View>
              <View>
                <PdFormLabel style={styles.mb1}>Sales Rep Commission</PdFormLabel>
                <View style={styles.pdInputGroup}>
                  <PdHookFormInputPercentage
                    style={styles.pdInputGroupInput}
                    name="salesRepCommission"
                    rules={{
                      validate: (value) => {
                        if (value === 0) return "Commission cannot be 0%";
                        if (!watched[2]) return true;
                        if (value === null) return "Commission is mandatory as you entered an email";
                        return true;
                      },
                    }}
                    onSubmitEditing={handleOnAcceptOrSave}
                  />
                  <PdText style={styles.mr2}>%</PdText>
                </View>
                <PdHookFormErrorMessage name="salesRepCommission" />
              </View>
            </PdStack>
          </View>
          <View>
            <PdText variant="body-sm" style={styles.helpText}>
              {
                "If you'd like to assign a Sales Rep to this retailer, please enter their email address and the commission you'd like them to receive on orders this Retailer will place. The Main Tab will facilitate the commission payout to the Sales Rep upon order shipment. Commission cannot be 0%."
              }
            </PdText>
            <PdText variant="body-sm" style={styles.helpTextEndGray}>
              {
                "Note: if the sales rep is inactive/doesn't have a Main Tab account, they will receive an invite to create an account. We will verify them and if approved, they will become 'Active'."
              }
            </PdText>
          </View>
        </PdStack>

        <PdStack style={styles.mt6} direction={rIsLargeScreen ? "horizontal" : "vertical"} isReversed={!rIsLargeScreen}>
          {isConnectionApproved ? (
            <PdButton
              colorScheme="red"
              variant="outline"
              onPress={handleOnDeactivate}
              style={styles.minWidthMd}
              isFullWidth={!rIsLargeScreen}
            >
              <PdButtonLabel>Deactivate Connection</PdButtonLabel>
            </PdButton>
          ) : (
            <PdButton
              variant="outline"
              isDisabled={isConnectionDeclined}
              onPress={handleOnDecline}
              style={styles.minWidthMd}
              isFullWidth={!rIsLargeScreen}
            >
              <PdButtonLabel>Decline Connection</PdButtonLabel>
            </PdButton>
          )}
          <PdButton onPress={handleOnAcceptOrSave} style={styles.saveButton} isFullWidth={!rIsLargeScreen}>
            <PdButtonLabel>{isConnectionApproved ? "Save & Update" : "Accept & Activate"}</PdButtonLabel>
          </PdButton>
        </PdStack>
      </View>
    </FormProvider>
  );
};

const Content: React.FC<{ retailer: tBoutique; brandRetailerConnection?: tUserConnection }> = ({
  retailer,
  brandRetailerConnection,
}) => {
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const rIsLargeScreen = useIsLargeScreenSelector();
  const rMessagesThreads = useSelector((state) => state.MessageThreadsReducer.rThreads);

  const address = getDefaultAddress(retailer.addresses);
  const category = retailer.primaryCategory ? dRetailerCategories[retailer.primaryCategory] : "";

  const handleOnPressLink = (link?: string) => {
    window.open(link, "_blank");
  };

  const handleOnPressMessage = () => {
    try {
      const rThreadId = rMessagesThreads.find((thread) => thread.recipient.includes(retailer.id));
      if (!rThreadId) throw new Error("An error occurred while attempting to parse the recipient's id.");
      dispatch(setCurrentThread(rThreadId));
      navigation.navigate(kRoute.P04);
    } catch (er) {
      dispatch({
        type: "RIBBON",
        message: new Error("Please use the left menu to send a message to the Retailer"),
      });
    }
  };

  return (
    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.mb20}>
      <PdProfileBanner />
      <View style={rIsLargeScreen ? styles.containerWeb : styles.containerMobile}>
        <PdProfileAvatar source={{ uri: retailer.avatar }} />
        <View style={rIsLargeScreen ? styles.contentWeb : styles.contentMobile}>
          <View style={rIsLargeScreen ? styles.flex1 : {}}>
            <PdText style={styles.nameTitle}>
              {retailer.contactPerson ?? `${retailer.owner.first_name} ${retailer.owner.last_name}`}
            </PdText>
            <PdText style={styles.storeTitle}>{retailer.name}</PdText>
            {address.city && address.state_or_province && (
              <PdText numberOfLines={1}>{`${address.city}, ${address.state_or_province}`}</PdText>
            )}
          </View>
          <View style={styles.messageButtonContainer}>
            <PdButton
              variant="outline"
              onPress={handleOnPressMessage}
              leftIcon={<PdMessage style={styles.messageIcon} />}
              style={rIsLargeScreen ? {} : styles.mt6}
            >
              <PdButtonLabel>Message</PdButtonLabel>
            </PdButton>
          </View>
        </View>
      </View>
      <PdStack
        style={rIsLargeScreen ? styles.cardsContainerWeb : styles.cardsContainerMobile}
        direction={rIsLargeScreen ? "horizontal" : "vertical"}
        gap={4}
        layout={[2, 1]}
      >
        <PdProfileCard>
          <PdText style={styles.cardTitle}>About Me</PdText>
          <PdText>{retailer.aboutMe || "-"}</PdText>
        </PdProfileCard>
        <PdProfileCard>
          {retailer.taxInfo?.url && (
            <TouchableOpacity onPress={() => handleOnPressLink(retailer.taxInfo?.url)} style={styles.profileButton}>
              <PdText>View Tax ID/Resale Certificate</PdText>
              <PdArrowUpRight style={styles.arrowUpIcon} color={kColors.brandDark} />
            </TouchableOpacity>
          )}
          <TouchableOpacity onPress={() => handleOnPressLink(retailer.website)} style={styles.profileButton}>
            <PdText>Website</PdText>
            <PdArrowUpRight style={styles.arrowUpIcon} color={kColors.brandDark} />
          </TouchableOpacity>
          <PdText style={styles.cardTitle}>Location</PdText>
          <PdText style={styles.mb3}>{longAddressToString(address)}</PdText>
          <PdText style={styles.cardTitle}>Socials</PdText>
          {retailer.links?.linkedin || retailer.links?.instagram || retailer.links?.facebook ? (
            <View style={styles.socialsContainer}>
              {retailer.links.linkedin && (
                <PdProfileSocialIconButton
                  style={styles.mr3}
                  onPress={() => handleOnPressLink(retailer.links.linkedin)}
                  icon={<PdLinkedin style={styles.sizeXs} />}
                />
              )}
              {retailer.links.instagram && (
                <PdProfileSocialIconButton
                  style={styles.mr3}
                  onPress={() => handleOnPressLink(retailer.links.instagram)}
                  icon={<PdInstagram style={styles.sizeXs} />}
                />
              )}
              {retailer.links.facebook && (
                <PdProfileSocialIconButton
                  onPress={() => handleOnPressLink(retailer.links.facebook)}
                  icon={<PdFacebookAlt style={styles.sizeXs} />}
                />
              )}
            </View>
          ) : (
            <PdText style={styles.mb3}>-</PdText>
          )}
          <PdText style={styles.cardTitle}>Retailer Category</PdText>
          {category ? (
            <View style={styles.categoriesContainer}>
              <PdProfileTag>
                <PdProfileTagLabel>{category}</PdProfileTagLabel>
              </PdProfileTag>
            </View>
          ) : (
            <PdText>-</PdText>
          )}
        </PdProfileCard>
      </PdStack>
      {brandRetailerConnection && <ConnectionTermsForm connection={brandRetailerConnection} />}
    </ScrollView>
  );
};

const P10_ViewRetailer: React.FC<{ route: RouteProp<tParams, "rConnection"> }> = ({ route }) => {
  const isFocused = useIsFocused();
  const rCompanyType = useSelector((state) => state.UserReducer.profile.accountType);
  const [retailer, setRetailer] = React.useState<tBoutique | undefined>(undefined);
  const [brandRetailerConnection, setBrandRetailerConnection] = React.useState<tUserConnection | undefined>(undefined);

  useFocusEffect(
    React.useCallback(() => {
      const unsubscribe = retailerDb.documentRetailer(route.params?.id).onSnapshot((document) => {
        setRetailer(document.data());
      });

      return () => {
        unsubscribe();
      };
    }, [route.params?.id]),
  );

  useFocusEffect(
    React.useCallback(() => {
      let unsubscribe: (() => void) | undefined;

      if (rCompanyType === "brand" || rCompanyType === "tmtAdmin") {
        unsubscribe = connectionDb
          .documentBrandRetailerConnection(route.params?.connectionId)
          .onSnapshot((document) => {
            setBrandRetailerConnection(document.data());
          });
      }

      return () => {
        unsubscribe?.();
      };
    }, [rCompanyType, route.params?.connectionId]),
  );

  React.useEffect(() => {
    if (!isFocused) {
      setRetailer(undefined);
      setBrandRetailerConnection(undefined);
    }
  }, [isFocused]);

  if (!retailer) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  return <Content retailer={retailer} brandRetailerConnection={brandRetailerConnection} />;
};

export const P10 = React.memo(P10_ViewRetailer);

const styles = StyleSheet.create({
  activeBadge: {
    backgroundColor: kColors.green200,
    marginLeft: 2 * kStyleConst.kSpacing,
  },
  alignStart: {
    alignItems: "flex-start",
  },
  arrowUpIcon: {
    height: 12,
    marginLeft: kStyleConst.kSpacing,
    width: 12,
  },
  assignShippingFormControl: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  cancelRequestButton: {
    marginLeft: "auto",
    minWidth: 191,
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: "400",
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  cardsContainerMobile: {
    alignItems: "flex-start",
    marginBottom: 6 * kStyleConst.kSpacing,
    marginHorizontal: 5 * kStyleConst.kSpacing,
  },
  cardsContainerWeb: {
    alignItems: "flex-start",
    marginBottom: 6 * kStyleConst.kSpacing,
    marginHorizontal: 20 * kStyleConst.kSpacing,
  },
  categoriesContainer: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  containerMobile: {
    marginBottom: 6 * kStyleConst.kSpacing,
    marginHorizontal: 5 * kStyleConst.kSpacing,
  },
  containerWeb: {
    flexDirection: "row",
    marginBottom: 6 * kStyleConst.kSpacing,
    marginHorizontal: 20 * kStyleConst.kSpacing,
  },
  contentMobile: {
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 3 * kStyleConst.kSpacing,
  },
  contentWeb: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    marginLeft: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  flex1: {
    flex: 1,
  },
  groundShippingFormControl: {
    flexDirection: "row",
    flex: 1,
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  helpText: {
    color: kColors.brandDark,
    marginBottom: 3 * kStyleConst.kSpacing,
    minWidth: 200,
  },
  helpTextEnd: {
    color: kColors.brandDark,
    minWidth: 200,
  },
  helpTextEndGray: {
    color: kColors.gray500,
    minWidth: 200,
  },
  linkLabel: {
    fontSize: 10,
    textDecorationLine: "underline",
  },
  loadingContainer: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  mb1: {
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  mb20: {
    marginBottom: 20 * kStyleConst.kSpacing,
  },
  mb3: {
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mb6: {
    marginBottom: 6 * kStyleConst.kSpacing,
  },
  messageButtonContainer: {
    alignItems: "flex-end",
    flex: 1,
  },
  messageIcon: {
    height: 18,
    width: 18,
  },
  minWidthMd: {
    minWidth: 191,
  },
  ml2: {
    marginLeft: 2 * kStyleConst.kSpacing,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mobileFormContainer: {
    marginBottom: 6 * kStyleConst.kSpacing,
    marginHorizontal: 5 * kStyleConst.kSpacing,
  },
  mobileFormLine: {
    minHeight: 150,
  },
  mr2: {
    marginRight: 2 * kStyleConst.kSpacing,
  },
  mr3: {
    marginRight: 3 * kStyleConst.kSpacing,
  },
  mt6: {
    marginTop: 6 * kStyleConst.kSpacing,
  },
  mx20: {
    marginHorizontal: 20 * kStyleConst.kSpacing,
  },
  mx5: {
    marginHorizontal: 5 * kStyleConst.kSpacing,
  },
  my6: {
    marginVertical: 6 * kStyleConst.kSpacing,
  },
  nameTitle: {
    fontSize: 22,
    fontWeight: "400",
    letterSpacing: 2,
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  pdInputGroup: {
    alignItems: "center",
    borderColor: kColors.gray300,
    borderWidth: 1,
    flexDirection: "row",
  },
  pdInputGroupInput: {
    borderWidth: 0,
    width: "100%",
  },
  pr3: {
    paddingRight: 3 * kStyleConst.kSpacing,
  },
  profileButton: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  px0: {
    paddingHorizontal: 0,
  },
  salesRepFormControl: {
    flexDirection: "row",
    flex: 1,
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  saveButton: {
    marginLeft: "auto",
    minWidth: 191,
  },
  shipAccountFormControl: {
    flexDirection: "row",
    flex: 1,
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  sizeSm: {
    width: 20,
  },
  sizeXs: {
    height: 12,
    width: 12,
  },
  socialsContainer: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  storeTitle: {
    fontSize: 18,
    fontWeight: "400",
    letterSpacing: 2,
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  webFormContainer: {
    marginHorizontal: 20 * kStyleConst.kSpacing,
  },
  webFormLine: {
    alignItems: "flex-start",
    minHeight: 150,
  },
});
